<template>
  <v-chart ref="chart" :options="options" :init="chartInit" :echart.sync="echart" :ready="chartReady" @contextmenu="contextmenu" />
</template>

<script>
import { format, parse, addDays } from 'date-fns';
import chartable from '@/mixins/chartable';
import motletable from '@/mixins/motletable';

export default {
  mixins: [chartable, motletable],
  props: {
    props: Object,
    system: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    values: {
      type: Array,
      required: true,
    },
    yAxisNames: {
      type: Array,
      required: true,
    },
    grid: {
      type: Object,
      default() {
        return {
          top: '20%',
          bottom: '20px',
          left: '30px',
          right: '5px',
        };
      },
    },
    tooltipContents: {
      type: Array,
      default() {
        return [];
      },
    },
    split: {
      type: Object,
      default() {
        return {};
      },
    },
    motlet: {
      type: Object,
      required: true,
    },
    dateFormat: {
      type: String,
      default: 'yyyy-MM-dd HH:mm:ss',
    },
    areaStyle: {
      type: String,
      default: 'normal',
    },
    contextmenu: {
      type: Function,
      default: e => e,
    },
  },
  data() {
    return {
      options: {},
      splits: [],
      xAxisData: [],
      chart: null,
      echart: null,
    };
  },
  computed: {
    chartInit() {
      const xAxisData = Array(24)
        .fill('')
        .map((item, index) => format(new Date(1900, 0, 1, index), 'yyyy-MM-dd HH:mm:ss'));
      return {
        grid: this.grid,
        xAxis: {
          type: 'category',
          axisLabel: {
            interval: (index, value) => index > 0 && value.lastIndexOf('00:00') == 14,
            formatter: value => format(parse(value, this.dateFormat, new Date()), 'HH'),
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
          data: xAxisData,
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: value => value,
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
          max: item => {
            let max = Math.floor(item.max + Math.ceil(item.max / 5));
            const size = String(max).length;
            if (size > 2) {
              max = parseInt(
                String(max).substring(0, 2) +
                  Array(size - 2)
                    .fill('0')
                    .join(''),
              );
            }
            return max;
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: { formatter: param => this.axisPointerLabelFormatter(param) },
          },
          formatter: params => this.tooltipFormatter(params),
          position: (pos, params, el, elRect, size) => {
            var obj = { top: 10 };
            obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 30;
            return obj;
          },
        },
        toolbox: {
          itemSize: 0,
          feature: {
            dataZoom: { yAxisIndex: 'none' },
          },
        },
      };
    },
    isMerged() {
      return this.items.every(item => item.active);
    },
    zooming() {
      const xAxis = this.echart?.getOption()?.xAxis[0];
      return xAxis?.rangeStart !== null && xAxis?.rangeEnd !== null;
    },
  },
  watch: {
    zooming: {
      handler: 'changeZoomingHandler',
    },
    zoomRestore: {
      handler: 'changeZoomRestoreHandler',
    },
    items: {
      deep: true,
      handler: 'changItemsHandler',
    },
    values: {
      handler: 'changeValuesHandler',
    },
    isMerged: {
      immediate: true,
      handler: 'changeIsMergedHandler',
    },
    isDark: {
      handler: 'changColorHandler',
    },
    split: {
      handler: 'changeSplitHandler',
    },
  },
  methods: {
    changItemsHandler() {
      this.changColorHandler();
    },
    changeValuesHandler() {
      this.$nextTick(() => {
        this.options = this.getOptions();
      });
    },
    changeSplitHandler() {
      // split 데이터를 큐에 저장한다.
      this.splits.push(this.split);
      const valueIndex = this.values.findIndex(value => value.id == this.split.id);
      // values 가 지정되기 이전에는 처리하지 않는다.
      if (valueIndex == -1) return;

      // db data 처리
      const value = (split, index) => {
        const stime = this.values[index].left[this.values[index].left.length - 1].stime;

        // 우측 데이터 (과거 데이터)가 비어있을 경우 : 좌측 데이터 (현재 데이터)를 과거 데이터로 이관한다.
        if (this.values[index].right.length == 0) {
          this.values[index].right.splice(0, 0, ...this.values[index].left); // 전체 추가
          this.values[index].left.splice(0, this.values[index].left.length); // 전체 삭제
        }

        // split time 이 과거 시점일 경우 값만 변경한다.
        if (split.stime <= stime) {
          const targetIndex = this.values[index].left.findIndex(value => value.stime == split.stime);
          // console.log('this.values', this.values);
          // console.log('stime, split.stime', stime, split.stime);
          // console.log('index, targetIndex', index, targetIndex);
          // console.log('this.values[index].left[targetIndex]', this.values[index].left[targetIndex]);
          this.yAxisNames.forEach(yAxisName => (this.values[index].left[targetIndex][yAxisName] = split[yAxisName] || 0));
          return;
        }

        // 추가될 좌측 데이터 추출 & 삭제
        const add = this.values[index].right.splice(0, 1);
        // y 좌표 이름 정보를 이용하여 데이터 대상을 발췌한다.
        this.yAxisNames.forEach(yAxisName => (add[yAxisName] = split[yAxisName]));
        add.stime = split.stime;
        // 좌측 데이터 추가.
        this.values[index].left.push(add);
      };

      // chart series data 처리
      const chart = (split, index) => {
        const series = this.echart.getOption().series;
        const seriesIndex = index * 2;
        // chart series 가 지정되기 이전에는 처리하지 않는다.
        if (!series[seriesIndex]) return;
        const stime = parse(this.xAxisData[series[seriesIndex].data.length - 1], this.dateFormat, new Date()).getTime();

        // 우측 데이터 (과거 데이터)가 비어있을 경우 : 좌측 데이터 (현재 데이터)를 과거 데이터로 이관한다.
        if (!series[seriesIndex + 1].data.length) {
          series[seriesIndex + 1].data.splice(0, 0, ...series[seriesIndex].data.map((val, index) => [index, val])); // 전체 추가
          series[seriesIndex].data.splice(0, series[seriesIndex].data.length); // 전체 삭제
        }

        // split time 이 과거 시점일 경우 값만 변경한다.
        if (split.stime <= stime) {
          const dataIndex = this.values[index].left.findIndex(value => value.stime == split.stime);
          this.yAxisNames.forEach((yAxisName, idx) => (series[seriesIndex].data[dataIndex][idx + 1] = split[yAxisName] || 0));
          return;
        }

        // 좌측 데이터를 추가한다.
        const splits = this.yAxisNames.map(yAxisName => split[yAxisName] || 0);
        series[seriesIndex].data.push([series[seriesIndex].data.length, ...splits]);

        // 우측 데이터를 삭제한다.
        series[seriesIndex + 1].data.splice(0, 1);
        // 인덱스를 재조정한다.
        series[seriesIndex + 1].data.forEach((item, idx) => (series[seriesIndex + 1].data[idx][0] = series[seriesIndex].data.length + idx));

        // 표시 라인 지정
        series[0].markLine = { data: [this.getMarkLine(this.xAxisData[series[seriesIndex].data.length - 1])] };
        this.echart.setOption({ series: series });
      };

      // split 큐 데이터를 처리한다.
      this.splits.forEach(split => {
        // BEGIN:: Modified.by.KWShin.2025.11.18.
        // const index = this.items.findIndex(item => item.id == split.id);
        const index = this.items.findIndex(item => {
          const id = item.instanceId == '' ? item.id : item.instanceId + item.id;
          return id == split.id;
        });
        // FINISH:: Modified.by.KWShin.2025.11.18.
        // queue 를 처리하는 도중에 instance 정보가 변경될때 해당 처리를 실행하지 않는다.
        if (index > -1) {
          value(split, index);
          chart(split, index);
        }
      });
      // split 큐 데이터를 삭제한다.
      this.splits = [];
    },
    changeIsMergedHandler() {
      if (!this.options.series) return;
      this.options.series.forEach((series, seriesIndex) => this.changeIsMergedItemHandler(series, this.getItemIndex(seriesIndex), seriesIndex));
    },
    changColorHandler() {
      if (!this.options.series) return;
      this.options.series.forEach((series, seriesIndex) => {
        const itemIndex = this.getItemIndex(seriesIndex);
        if (!seriesIndex) {
          series.markLine.data[0].lineStyle.color = this.getReverseThemeColor(this.chartColor.splitArea, 10, 30);
        }
        if (this.isMerged) this.changeIsMergedItemHandler(series, itemIndex, seriesIndex);
        else this.changColorItemHandler(series, itemIndex, seriesIndex);
      });
    },
    changeIsMergedItemHandler(series, itemIndex, seriesIndex) {
      series.stack = this.getStack(seriesIndex);
      const item = this.items[itemIndex];
      const left = (series, itemIndex) => {
        if (itemIndex == this.items.length - 1) {
          const formatter = param =>
            this.values.reduce((acc, value) => {
              const column = this.yAxisNames;
              return (acc += (value.left[param.data.coord[0]] || {})[column[0]] || 0);
            }, 0);
          const color = this.isMerged ? this.chartColor.merge : item.color;
          const opacity = this.isMerged ? 0.8 : item.active ? 0.8 : 0.1;
          series.lineStyle.color = color;
          series.itemStyle.color = color;
          series.lineStyle.opacity = opacity;
          series.itemStyle.opacity = opacity;
          series.symbolSize = 10;
          series.markPoint.data[0].label.formatter = this.isMerged ? formatter : {};
          series.markPoint.data[0].label.color = this.getReverseThemeColor(this.isMerged ? this.chartColor.splitArea : this.chartColor.splitArea, 10, 60);
          series.markPoint.data[0].itemStyle.color = color;
          series.markPoint.data[0].itemStyle.opacity = this.isMerged ? 0.5 : item.active ? 0.5 : 0.1;
        } else {
          series.markPoint.data[0].label.show = this.isMerged ? false : true;
          series.symbolSize = this.isMerged ? 0 : 10;
          if (this.isMerged) {
            series.lineStyle.opacity = 0;
            series.itemStyle.opacity = 0;
            series.markPoint.data[0].itemStyle.opacity = 0;
          } else {
            this.changColorItemHandler(series, itemIndex);
          }
        }
      };
      const right = (series, itemIndex) => {
        if (itemIndex == this.items.length - 1) {
          const formatter = param => {
            return this.values.reduce((acc, value) => {
              const column = this.yAxisNames;
              // 우측 series 는 별도의 독립적인 index 로 구성되어 있으므로,
              // 우측/좌측이 통합되어 구성된 db values (this.values) 의 값을 참조하고자 한다면,
              // 좌측 series 의 개수를 제외하여 우측 series 의 값 index 를 산출한다.
              const index = param.data.coord[0] - value.left.length;
              acc += (value.right[index] || {})[column[0]] || 0;
              return acc;
            }, 0);
          };
          const color = this.getGrayColor(this.isMerged ? this.chartColor.merge : item.color);
          const opacity = this.isMerged ? 0.8 : item.active ? 0.8 : 0.1;
          series.lineStyle.opacity = opacity;
          series.itemStyle.opacity = opacity;
          series.symbolSize = 10;
          series.markPoint.data[0].label.formatter = this.isMerged ? formatter : {};
          series.markPoint.data[0].label.color = this.getReverseThemeColor(this.isMerged ? this.chartColor.splitArea : this.chartColor.splitArea, 10, 60);
          series.markPoint.data[0].itemStyle.color = color;
          series.markPoint.data[0].itemStyle.opacity = this.isMerged ? 0.5 : item.active ? 0.5 : 0.1;
        } else {
          series.symbolSize = this.isMerged ? 0 : 10;
          if (this.isMerged) {
            series.lineStyle.opacity = 0;
            series.itemStyle.opacity = 0;
            series.markPoint.data[0].itemStyle.opacity = 0;
          } else {
            this.changColorItemHandler(series, itemIndex);
          }
        }
      };
      if (seriesIndex % 2) right(series, itemIndex);
      else left(series, itemIndex);
    },
    changColorItemHandler(series, itemIndex) {
      const item = this.items[itemIndex];
      series.lineStyle.opacity = item.active ? 0.8 : 0.1;
      series.itemStyle.opacity = item.active ? 0.8 : 0.1;
      series.markPoint.data[0].label.color = this.getReverseThemeColor(this.chartColor.splitArea, 10, 60);
      series.markPoint.data[0].itemStyle.opacity = item.active ? 0.5 : 0.1;
    },
    chartReady(echart) {
      echart.dispatchAction({
        type: 'takeGlobalCursor',
        key: 'dataZoomSelect',
        dataZoomSelectActive: true,
      });
    },
    changeZoomingHandler(active) {
      this.$store.dispatch('common/motlet/setZoomActive', { id: this.motletId, active });
    },
    changeZoomRestoreHandler() {
      if (this.zoomRestore) {
        this.echart.dispatchAction({ type: 'restore' });
        this.$store.dispatch('common/motlet/setZoomRestore', { id: this.motletId, restore: false });
      }
    },
    getOptions() {
      // console.log('in LineDoubleStack.vue, this.values', this.values);
      let seriesIndex = 0;
      this.xAxisData = this.values[0].left.concat(this.values[0].right).map(value => format(new Date(value.stime), this.dateFormat));
      const series = this.items.reduce((acc, item, itemIndex) => {
        // BEGIN:: Modified.by.KWShin.2025.11.18.
        // instance id 에 해당하는 데이터
        // const value = this.values.find(value => item.id == value.id);
        const value = this.values.find(value => {
          const id = item.instanceId == '' ? item.id : item.instanceId + item.id;
          return id == value.id;
        });
        // FINISH:: Modified.by.KWShin.2025.11.18.
        acc.push(this.getSeries(value, itemIndex, seriesIndex++));
        acc.push(this.getSeries(value, itemIndex, seriesIndex++));
        return acc;
      }, []);
      // y 좌표 max 값 산출
      // const yMax = (list => {
      //   // left/right 로 분리된 데이터를 통합힌다.
      //   const series = [];
      //   list.forEach((item, index) => {
      //     if (index && index % 2) series.push(list[index - 1].data.concat(item.data));
      //   });
      //   // y 좌표에 해당하는 index 1 데이터를 합산한다.
      //   const merge = series.reduce((acc, item, index) => {
      //     if (index == 0) acc = item;
      //     else
      //       acc = item.map((data, seq) => {
      //         const concat = data.slice();
      //         concat[1] += acc[seq][1];
      //         return concat;
      //       });
      //     return acc;
      //   }, []);
      //   return merge.reduce((acc, item) => (item[1] > acc ? item[1] : acc), 0);
      // })(series);
      // console.log(yMax);
      return {
        xAxis: {
          data: this.xAxisData,
        },
        series,
      };
    },
    getStack(seriesIndex) {
      return this.isMerged ? (seriesIndex % 2 ? 'right' : 'left') : `${seriesIndex}`;
    },
    getSeries(value, itemIndex, seriesIndex) {
      const item = this.items[itemIndex];
      // Added.by.KWShin.2025.11.18.
      const itemId = item.instanceId == '' ? item.id : item.instanceId + item.id;
      const left = (seriesData, itemIndex, seriesIndex) => ({
        // BEGIN:: Modified.by.KWShin.2025.11.18.
        // name: `${item.id}_left`,
        name: `${itemId}_left`,
        // FINISH:: Modified.by.KWShin.2025.11.18.
        type: 'line',
        stack: this.getStack(seriesIndex),
        lineStyle: { color: item.color, opacity: 0.8 },
        itemStyle: { color: item.color, opacity: 0.8 },
        markPoint: this.getMarkPoint(item.color),
        markLine: {
          symbol: 'triangle',
          symbolRotate: 180,
          z: 20,
          data: !seriesIndex ? [this.getMarkLine(value.left[value.left.length - 1].stime)] : [],
        },
        data: value.left.map((data, index) => {
          const values = this.yAxisNames.map(yAxisName => data[yAxisName] || 0); // idCount와 sessionCount를 분리해내는 과정이군...
          return [index, ...values];
        }),
      });
      const right = (seriesData, itemIndex, seriesIndex) => {
        const color = this.getGrayColor(item.color);
        return {
          // BEGIN:: Modified.by.KWShin.2025.11.18.
          // name: `${item.id}_right`,
          name: `${itemId}_right`,
          // FINISH:: Modified.by.KWShin.2025.11.18.
          type: 'line',
          stack: this.getStack(seriesIndex),
          lineStyle: { color, opacity: 0.8 },
          itemStyle: { color, opacity: 0.8 },
          markPoint: this.getMarkPoint(color),
          data: value.right.map((data, index) => {
            const xAxisIndex = value.left.length + index;
            const values = this.yAxisNames.map(yAxisName => data[yAxisName] || 0);
            return [xAxisIndex, ...values];
          }),
        };
      };
      if (!value) return { type: 'line' };
      const series = seriesIndex % 2 ? right(value, itemIndex, seriesIndex) : left(value, itemIndex, seriesIndex);
      this.changeIsMergedItemHandler(series, itemIndex, seriesIndex);
      return series;
    },
    getMarkPoint(color) {
      return {
        data: [
          {
            type: 'max',
            label: {
              show: true,
              color: this.getReverseThemeColor(this.chartColor.splitArea, 10, 60),
              fontWeight: 'normal',
              fontSize: 11,
              formatter: {},
            },
            itemStyle: { color, opacity: 0.5 },
          },
        ],
      };
    },
    getMarkLine(xAxisTime) {
      return {
        symbol: 'none',
        lineStyle: {
          color: this.getReverseThemeColor(this.chartColor.splitArea),
          width: 2,
          type: 'solid',
        },
        xAxis: format(new Date(xAxisTime), this.dateFormat),
        label: {
          distance: [0, 10],
          formatter: () => format(new Date(xAxisTime), 'HH:mm:ss'),
        },
      };
    },
    tooltipFormatter(params) {
      let list = [];
      let label = params[0].axisValue;
      const date = parse(label, this.dateFormat, new Date());
      if (this.split.stime < date.getTime()) label = format(addDays(date, -1), this.dateFormat);

      const contentsList = params =>
        params.map(param => {
          const seriesNameArray = param.seriesName.split('_');
          const seriesName = seriesNameArray[0];
          // BEGIN:: Modified.by.KWShin.2025.11.18.
          // const item = this.items.find(item => item.id == seriesName);
          const item = this.items.find(item => {
            const itemId = item.instanceId == '' ? item.id : item.instanceId + item.id;
            return itemId == seriesName;
          });
          // FINISH:: Modified.by.KWShin.2025.11.18.
          if (!item) return '';
          // let contents = [this.circle(item.color), `<span style="color:${item.color};font-weight:900;">`, seriesName, '</span>'];
          let contents = [this.circle(item.color), `<span style="color:${item.color};font-weight:900;">`, item.shortAlias, '</span>'];
          this.tooltipContents.forEach((tooltip, index) => {
            if (index) contents.push(' , ');
            else contents.push(' ');
            if (tooltip.name) contents = contents.concat([tooltip.name]);
            contents = contents.concat([' : ', param.data[index + 1]]);
            if (tooltip.unit) contents = contents.concat([' ', tooltip.unit]);
          });
          if (!this.tooltipContents.length) contents = contents.concat([': ', param.data[1]]);
          return contents.join('');
        });

      if (this.isMerged) {
        const sum = index => params.reduce((acc, param) => (acc += param.data[index + 1]), 0);
        let contents = [this.circle(this.chartColor.merge), `<span style="color:${this.chartColor.merge};font-weight:900;">`, this.system.id, '</span>'];
        this.tooltipContents.forEach((content, index) => {
          if (index) contents.push(' , ');
          else contents.push(' ');
          if (content.name) contents = contents.concat([content.name]);
          contents = contents.concat([' : ', sum(index)]);
          if (content.unit) contents = contents.concat([' ', content.unit]);
        });
        if (!this.tooltipContents.length) contents = contents.concat([': ', params.reduce((acc, param) => (acc += param.value), 0)]);
        list.push(contents.join(''));
        list = list.concat(contentsList(params));
      } else {
        // 활성화 된 item 만 표시
        list = contentsList(
          params.filter(param => {
            const seriesNameArray = param.seriesName.split('_');
            const seriesName = seriesNameArray[0];
            // BEGIN:: Modified.by.KWShin.2025.11.18.
            // return this.items.find(item => item.id == seriesName).active;
            const thisItem = this.items.find(item => {
              const itemId = item.instanceId == '' ? item.id : item.instanceId + item.id;
              return itemId == seriesName;
            });
            return thisItem.active;
            // FINISH:: Modified.by.KWShin.2025.11.18.
          }),
        );
      }
      list.unshift(label);
      return list.join('<br/>');
    },
    axisPointerLabelFormatter(param) {
      let label = '';
      if (param.axisDimension == 'y') label = param.value.toFixed(0);
      else {
        const date = parse(param.value, this.dateFormat, new Date());
        if (this.split.stime < date.getTime()) label = format(addDays(date, -1), this.dateFormat);
        else label = param.value;
      }
      return label;
    },
    getItemIndex(seriesIndex) {
      return (seriesIndex - (seriesIndex % 2)) / 2;
    },
  },
};
</script>
